import { Activity, useState } from "react";
import { format } from "date-fns";
import { ChevronIcon } from "vega-icons";

import { type DealFinancialReturnsResponse } from "@vega-api/generated/types/dealFinancialReturnsResponse";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { compareDatesDesc } from "@vega-core/utils/compareDates";

import {
  computeHistoryDeltas,
  formatPeriodLabel,
  type HistoryRow,
} from "../../utils/formatFinancialReturns";

import { ReportingHistoryTable } from "./ReportingHistoryTable";
import { ReportSectionMessage } from "./ReportSectionMessage";
import { SnapshotFormModal } from "./SnapshotFormModal";

export function ReportingHistorySection({
  data,
  isError,
  isPending,
  onRetry,
  editContext,
}: {
  data: DealFinancialReturnsResponse | undefined;
  isError: boolean;
  isPending: boolean;
  onRetry: () => void;
  editContext?: { dealId: string };
}) {
  const [editingSnapshot, setEditingSnapshot] = useState<HistoryRow | null>(
    null,
  );
  const { value: isExpanded, toggle } = useBoolean(true);

  const history = data?.history ?? [];
  const enrichedHistory = computeHistoryDeltas(history);

  const newestEntry = history.at(0);
  const oldestEntry = history.at(-1);
  const lastUpdatedAt = history.reduce<string | null>(
    (latest, entry) =>
      compareDatesDesc(entry.updatedAt, latest) < 0 ? entry.updatedAt : latest,
    null,
  );

  return (
    <div className="border border-border/primary rounded-containerCorner">
      <button
        aria-controls="reporting-history-content"
        aria-expanded={isExpanded}
        className={cnTwMerge(
          "relative z-10 w-full flex items-center justify-between gap-2 card-paddings-default rounded-lg focus-outline-themed",
          isExpanded && "border-b border-border/primary rounded-b-none",
        )}
        onClick={toggle}
        type="button"
      >
        <div>
          <h2 className="heading-5 text-left">
            {m.coInvestments_report_reportingHistory_title()}
          </h2>
          {!isPending && !isError && newestEntry && oldestEntry ? (
            <p className="body-small-regular text-text/secondary text-left">
              {m.coInvestments_report_reportingHistory_subtitle({
                count: history.length,
                from: formatPeriodLabel(oldestEntry.periodDate),
                to: formatPeriodLabel(newestEntry.periodDate),
              })}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!isPending && !isError && lastUpdatedAt ? (
            <p className="body-small-regular text-text/secondary">
              {m.coInvestments_report_reportingHistory_lastUpdated({
                period: format(new Date(lastUpdatedAt), "d MMM yyyy"),
              })}
            </p>
          ) : null}
          <ChevronIcon
            className={cnTwMerge(
              "transition-transform",
              !isExpanded && "rotate-180",
            )}
          />
        </div>
      </button>

      <Activity mode={isExpanded ? "visible" : "hidden"}>
        <div id="reporting-history-content">
          {isError ? (
            <ReportSectionMessage
              dataTestId="reporting-history-error"
              message={m.common_error_somethingWentWrong()}
              onRetry={onRetry}
            />
          ) : (
            <ReportingHistoryTable
              borderless
              history={enrichedHistory}
              isLoading={isPending}
              onEdit={editContext ? setEditingSnapshot : undefined}
            />
          )}
        </div>
      </Activity>

      {editContext && editingSnapshot !== null ? (
        <SnapshotFormModal
          dealId={editContext.dealId}
          onClose={() => setEditingSnapshot(null)}
          snapshot={editingSnapshot}
        />
      ) : null}
    </div>
  );
}
